import { createTheme } from "@material-ui/core/styles";
import theme, { colors } from "theme";

export const datePickerTokens = {
  nav: {
    activeBackground: colors.purple[100],
    activeIcon: colors.purple[700],
    inactiveBackground: colors.white,
    inactiveIcon: colors.gray[600],
  },
  day: {
    text: colors.gray[800],
    inactiveText: colors.gray[500],
    hoverBackground: "rgba(0, 0, 0, 0.08)",
    hoverText: colors.gray[800],
    selectedBackground: colors.purple[700],
    selectedFocusedBackground: colors.purple[900],
    selectedText: colors.white,
    todayBorder: colors.charcoal.soft,
  },
  range: {
    background: colors.purple[0],
    text: colors.gray[800],
    hoverBackground: "rgba(0, 0, 0, 0.08)",
    hoverText: colors.gray[800],
  },
  clock: {
    faceBackground: colors.gray[50],
    hand: colors.accentPurple,
    selectedBackground: colors.purple[700],
  },
  popper: {
    shadow: "0px 8px 24px rgba(35, 31, 32, 0.18)",
  },
};

export const datePickerTheme = createTheme(theme, {
  palette: {
    primary: {
      main: datePickerTokens.day.selectedBackground,
      dark: datePickerTokens.day.selectedFocusedBackground,
      contrastText: datePickerTokens.day.selectedText,
    },
    secondary: {
      main: datePickerTokens.nav.activeBackground,
    },
    background: {
      paper: colors.white,
      default: colors.white,
    },
    text: {
      primary: datePickerTokens.day.text,
      secondary: datePickerTokens.day.inactiveText,
    },
  },
  overrides: {
    MuiPickersPopper: {
      paper: {
        borderRadius: 0,
        boxShadow: datePickerTokens.popper.shadow,
      },
    },
    MuiPickersDay: {
      root: {
        color: datePickerTokens.day.text,
        fontSize: 12,
        fontWeight: 400,
        "&:hover": {
          backgroundColor: datePickerTokens.day.hoverBackground,
          color: datePickerTokens.day.hoverText,
        },
        "&$selected": {
          backgroundColor: datePickerTokens.day.selectedBackground,
          color: datePickerTokens.day.selectedText,
          fontWeight: 400,
        },
        "&$selected:hover": {
          backgroundColor: datePickerTokens.day.selectedFocusedBackground,
        },
        "&:focus": {
          backgroundColor: datePickerTokens.day.hoverBackground,
        },
        "&:focus$selected": {
          backgroundColor: datePickerTokens.day.selectedFocusedBackground,
        },
      },
      dayOutsideMonth: {
        color: datePickerTokens.day.inactiveText,
      },
      today: {
        "&:not($selected)": {
          border: `1px solid ${datePickerTokens.day.todayBorder}`,
        },
      },
    },
    MuiPickersCalendar: {
      weekDayLabel: {
        color: datePickerTokens.nav.inactiveIcon,
        fontSize: 14,
      },
    },
    MuiPickersDateRangeDay: {
      rangeIntervalDayHighlight: {
        backgroundColor: datePickerTokens.range.background,
        color: datePickerTokens.range.text,
      },
      dayInsideRangeInterval: {
        color: datePickerTokens.range.text,
      },
      notSelectedDate: {
        backgroundColor: "transparent",
        "&:hover": {
          backgroundColor: datePickerTokens.range.hoverBackground,
          color: datePickerTokens.range.hoverText,
        },
      },
      dayOutsideRangeInterval: {
        "&:hover": {
          backgroundColor: datePickerTokens.day.hoverBackground,
          color: datePickerTokens.day.hoverText,
        },
      },
    },
    MuiPickersCalendarHeader: {
      root: {
        marginTop: 12,
        paddingLeft: 24,
        paddingRight: 12,
        "& .MuiIconButton-root": {
          color: datePickerTokens.nav.inactiveIcon,
        },
      },
      monthTitleContainer: {
        color: datePickerTokens.day.text,
      },
      monthText: {
        color: datePickerTokens.day.text,
        fontSize: 16,
        fontWeight: 400,
      },
      switchViewDropdown: {
        color: datePickerTokens.nav.inactiveIcon,
      },
    },
    MuiPickersArrowSwitcher: {
      iconButton: {
        color: datePickerTokens.nav.inactiveIcon,
        backgroundColor: colors.white,
        "&:hover": {
          backgroundColor: "transparent",
        },
      },
    },
    MuiDateTimePickerTabs: {
      tabs: {
        backgroundColor: datePickerTokens.nav.inactiveBackground,
        color: datePickerTokens.nav.inactiveIcon,
        position: "relative",
        minHeight: 52,
        "& .MuiTabs-indicator": {
          display: "none",
        },
        "& button[role='tab']": {
          color: datePickerTokens.nav.inactiveIcon,
          backgroundColor: datePickerTokens.nav.inactiveBackground,
          maxWidth: "none",
          minWidth: "50%",
          minHeight: 52,
          opacity: 1,
          zIndex: 1,
          "& svg": {
            color: datePickerTokens.nav.inactiveIcon,
          },
        },
        "& button[role='tab'][aria-selected='true']": {
          backgroundColor: datePickerTokens.nav.activeBackground,
          color: datePickerTokens.nav.activeIcon,
          "& svg": {
            color: datePickerTokens.nav.activeIcon,
          },
        },
        "& button[role='tab'] svg": {
          fontSize: 22,
        },
        "& button[role='tab'] span": {
          position: "relative",
          zIndex: 1,
        },
      },
    },
    MuiPickersClock: {
      clock: {
        backgroundColor: datePickerTokens.clock.faceBackground,
      },
      pin: {
        backgroundColor: datePickerTokens.clock.hand,
      },
    },
    MuiPickersClockPointer: {
      pointer: {
        backgroundColor: datePickerTokens.clock.hand,
      },
      thumb: {
        border: `14px solid ${datePickerTokens.clock.selectedBackground}`,
      },
      noPoint: {
        backgroundColor: datePickerTokens.clock.selectedBackground,
      },
    },
    MuiPickersClockNumber: {
      root: {
        color: datePickerTokens.day.text,
      },
      clockNumberSelected: {
        backgroundColor: datePickerTokens.clock.selectedBackground,
        color: datePickerTokens.day.selectedText,
      },
    },
  },
});
